import { ChartData } from "@/dashboard/sales/monthly_breakdown_base"

/**
 * Redistributes "Uncoded" and "Misc" amounts proportionally to other expense account groups
 * @param chartData - Array of monthly chart data objects
 * @returns New chart data with redistributed amounts
 */
export function redistributeUncodedAndMisc(chartData: ChartData[]): ChartData[] {
  if (!chartData.length) return chartData

  // Get all unique expense account groups from the chart data
  const allGroups = new Set<string>()
  chartData.forEach(monthData => {
    Object.keys(monthData).forEach(key => {
      if (key !== "month") {
        allGroups.add(key)
      }
    })
  })

  return chartData.map(monthData => {
    const newMonthData: ChartData = { month: monthData.month }

    // Find Uncoded and Misc amounts for this month
    const uncodedAmount = (monthData["Uncoded"] as number) || 0
    const miscAmount = (monthData["Misc"] as number) || 0
    const totalRedistributeAmount = uncodedAmount + miscAmount

    // Get all other groups (excluding Uncoded and Misc)
    const otherGroups = Array.from(allGroups).filter(group => group !== "Uncoded" && group !== "Misc")

    if (otherGroups.length === 0 || totalRedistributeAmount === 0) {
      // If no other groups or nothing to redistribute, keep original data
      Object.keys(monthData).forEach(key => {
        if (key !== "month") {
          newMonthData[key] = monthData[key]
        }
      })
      return newMonthData
    }

    // Calculate total amount of other groups for this month
    const totalOtherAmount = otherGroups.reduce((sum, group) => {
      return sum + ((monthData[group] as number) || 0)
    }, 0)

    // Redistribute Uncoded and Misc amounts proportionally
    otherGroups.forEach(group => {
      const originalAmount = (monthData[group] as number) || 0
      const redistributedAmount =
        totalOtherAmount > 0
          ? originalAmount + totalRedistributeAmount * (originalAmount / totalOtherAmount)
          : originalAmount
      newMonthData[group] = redistributedAmount
    })

    return newMonthData
  })
}
